import {
  ActionError,
  AttentionBlock,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  FieldGrid,
  FormBlock,
  PageHeader,
  PageTitle,
  ReadOnlyField,
  ReadingColumn,
  StatusTag,
  Textarea,
  formatDate,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { FormSkeleton } from '@/features/clinic-profile/blocks'
import { ActionCard } from '@/features/users/action-card'
import { MSG_JUSTIFICATION, isJustification } from '@/features/users/labels'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import { useRemoveFromTeam, useResendTeamInvite, useTeamMember, type TeamMember } from './api'
import {
  OUTSIDE_EMAIL_EFFECT,
  REMOVE_ANCHOR,
  inviteText,
  lastSignInText,
  teamStatus,
} from './labels'

/**
 * A pessoa da equipe da Westhetics (docs/areas/administracao.md, "Pessoa da equipe"; RN-AUT-28 a
 * 30). Arquétipo: cadastro, só leitura: nome e e-mail não mudam por aqui. O que se faz são as duas
 * ações em cartão à parte: reenviar o convite de quem ainda não entrou e tirar da equipe, com
 * justificativa (RN-AUT-15). A própria conta não tem a segunda.
 */

export function TeamMemberPage() {
  const { id } = useParams()
  const query = useTeamMember(id)
  const member = query.data
  const notFound = isApiError(query.error) && query.error.status === 404
  const name = member ? titleCase(member.name) : '…'

  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[{ label: 'Equipe', href: routes.admin.team }, { label: name }]}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to={routes.admin.team}>Voltar</Link>
          </Button>
        }
      />
      <Page>
        <ReadingColumn wide>
          {notFound ? (
            <EmptyState
              className="mt-6"
              title="Esta pessoa não está na equipe"
              description="Ela pode ter saído da equipe. A conta e o que ela fez continuam registrados."
              action={
                <Button variant="outline" asChild>
                  <Link to={routes.admin.team}>Ver a equipe</Link>
                </Button>
              }
            />
          ) : query.isError ? (
            <>
              <PageTitle title="Pessoa da equipe" />
              <LoadError
                className="mt-6"
                title="Não conseguimos abrir esta pessoa"
                error={query.error}
                onRetry={() => void query.refetch()}
              />
            </>
          ) : !member ? (
            <>
              <PageTitle title="Pessoa da equipe" />
              <FormSkeleton blocks={2} />
            </>
          ) : (
            <MemberDetail key={member.id} member={member} />
          )}
        </ReadingColumn>
      </Page>
    </>
  )
}

function MemberDetail({ member }: { member: TeamMember }) {
  const session = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const resend = useResendTeamInvite(member.id)
  const remove = useRemoveFromTeam(member.id)
  const name = titleCase(member.name)
  const status = teamStatus(member)
  const isSelf = member.id === session.profile?.userId

  const [failure, setFailure] = useState<{ title: string; description: string } | null>(null)
  // "tirar da equipe" na lista chega aqui com a âncora: o cartão já abre com a justificativa.
  const [removing, setRemoving] = useState(location.hash === `#${REMOVE_ANCHOR}` && !isSelf)
  const [justification, setJustification] = useState('')
  const [attempted, setAttempted] = useState(false)
  const [confirming, setConfirming] = useState(false)

  function resendInvite() {
    setFailure(null)
    resend.mutate(undefined, {
      onSuccess: (result) => {
        if (result.emailSent) {
          toast(`Convite reenviado a ${member.email} — vale 7 dias`)
          return
        }
        setFailure({
          title: 'O convite foi criado, mas o e-mail não saiu',
          description: `Reenvie o convite em instantes. Se continuar falhando, confira o endereço ${member.email}.`,
        })
      },
      onError: (error) =>
        setFailure({
          title: 'Não foi possível reenviar o convite',
          description: errorMessage(error),
        }),
    })
  }

  function askRemove() {
    setAttempted(true)
    if (isJustification(justification)) setConfirming(true)
  }

  function confirmRemove() {
    remove.mutate(justification.trim(), {
      onSuccess: () => {
        toast(`${name} não é mais da equipe da Westhetics — o histórico continua registrado`)
        navigate(routes.admin.team)
      },
      onError: (error) => {
        setConfirming(false)
        setFailure({ title: 'Não foi possível tirar da equipe', description: errorMessage(error) })
      },
    })
  }

  return (
    <>
      <PageTitle
        title={name}
        meta={`${member.email} · ${
          member.lastSignInAt
            ? `último acesso ${lastSignInText(member.lastSignInAt)}`
            : 'ainda não entrou'
        }`}
        trailing={<StatusTag level={status.level}>{status.label}</StatusTag>}
      />

      <div className="mt-6 flex flex-col gap-4">
        {failure ? (
          <ActionError
            title={failure.title}
            description={failure.description}
            onDismiss={() => setFailure(null)}
          />
        ) : null}

        {member.hasAllowedEmail ? null : (
          <AttentionBlock
            title={`O e-mail ${member.email} é de fora da empresa: ${isSelf ? 'você vai' : `${name} vai`} sair da equipe`}
          >
            {OUTSIDE_EMAIL_EFFECT}
          </AttentionBlock>
        )}

        <FormBlock
          title="Acesso"
          note="Quem é da equipe vê todas as clínicas e, ao entrar numa delas, age como gestora. Nome e e-mail não mudam por aqui."
        >
          <FieldGrid>
            <ReadOnlyField label="Nome" value={name} note={null} />
            <ReadOnlyField label="E-mail" value={member.email} note={null} />
            <ReadOnlyField
              label="Conta criada"
              value={`${formatDate(new Date(member.createdAt))} por ${titleCase(member.createdBy.name)}`}
              note={null}
            />
            <ReadOnlyField
              label="Clínicas"
              value={
                member.clinicCount === 0
                  ? 'Sem vínculo com clínica'
                  : member.clinicCount === 1
                    ? 'Também tem vínculo com 1 clínica'
                    : `Também tem vínculo com ${member.clinicCount} clínicas`
              }
              note={null}
            />
          </FieldGrid>
        </FormBlock>

        {member.status === 'invited' ? (
          <ActionCard
            title="Reenviar convite"
            description={`${inviteText(member) ?? `${name} ainda não entrou.`} Reenviar gera um link novo, válido por 7 dias; o anterior e a senha temporária, se houver, deixam de valer.`}
            action={
              <Button
                variant="outline"
                onClick={resendInvite}
                loading={resend.isPending}
                loadingLabel="Reenviando…"
              >
                Reenviar convite
              </Button>
            }
          />
        ) : null}

        {isSelf ? (
          <ActionCard
            title="Sua conta"
            description="Ninguém tira a própria conta da equipe. Para sair, peça a outra pessoa da equipe."
            action={null}
          />
        ) : (
          <ActionCard
            id={REMOVE_ANCHOR}
            title="Tirar da equipe"
            description="A pessoa perde na hora o acesso da equipe: deixa de ver as clínicas e o painel da Westhetics. O que ela fez continua registrado, e os vínculos com clínicas, se houver, continuam."
            action={
              removing ? null : (
                <Button
                  variant="outline"
                  onClick={() => {
                    setFailure(null)
                    setRemoving(true)
                  }}
                >
                  Tirar da equipe
                </Button>
              )
            }
          >
            {removing ? (
              <>
                <Field
                  label="Justificativa"
                  meta={`${justification.trim().length}/500`}
                  error={
                    attempted && !isJustification(justification) ? MSG_JUSTIFICATION : undefined
                  }
                  hint="Fica na trilha da Westhetics, com o seu nome e a data."
                >
                  <Textarea
                    autoFocus
                    value={justification}
                    maxLength={500}
                    onChange={(event) => setJustification(event.target.value)}
                  />
                </Field>
                <div className="flex flex-wrap items-center gap-4">
                  <Button variant="outline" onClick={askRemove}>
                    Tirar da equipe
                  </Button>
                  <Button variant="text" onClick={() => setRemoving(false)}>
                    Desistir
                  </Button>
                </div>
              </>
            ) : null}
          </ActionCard>
        )}
      </div>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Tirar ${name} da equipe da Westhetics?`}
        effect="As sessões abertas desta pessoa são encerradas agora, e ela deixa de ver o painel e as clínicas. Tudo o que ela fez continua registrado; a conta e os vínculos com clínicas continuam."
        confirmLabel="Tirar da equipe"
        loading={remove.isPending}
        loadingLabel="Tirando da equipe…"
        onConfirm={confirmRemove}
      />
    </>
  )
}
